import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const TimestampFormat: React.FC = () => {
    const [copied, setCopied] = useState(false);
    const exampleFormat = "YYYY-MM-DD:hh:mm:ss";
    const exampleTimestamp = "2025-06-05:13:04:48";

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box className="flex flex-col border-b border-gray-200" id="timestamp-format">
            <Typography variant="h6" fontWeight={600} fontSize={16} mb={1}>
                TIMESTAMP FORMAT
            </Typography>
            
            <Typography variant="body2" fontSize={14} fontWeight={400} mb={2}>
                For the timestamp, it's crucial to adhere to the specified format: <strong>YYYY-MM-DD:hh:mm:ss</strong>. You should retrieve this timestamp directly from the alert packet data you receive.
            </Typography>
            <Typography variant='body2' marginBottom={1} fontSize={14} fontWeight={600}>Format</Typography>
            <Box sx={{ 
                backgroundColor: '#393942ff',
                border: '1px solid #45475a',
                borderRadius: 2,
                overflow: 'hidden',
                marginBottom: 2
            }}>
                <Box sx={{ 
                    backgroundColor: '#393943ff',
                    borderBottom: '1px solid #45475a',
                    px: 2,
                    py: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography 
                        variant="body2" 
                        fontSize={12}
                        sx={{ 
                            color: '#a6adc8',
                            fontWeight: 500
                        }}
                    >
                        Required Format
                    </Typography>
                    <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
                        <IconButton 
                            size="small" 
                            onClick={() => handleCopy(exampleFormat)}
                            sx={{ 
                                color: '#a6adc8',
                                '&:hover': { 
                                    backgroundColor: '#40414aff',
                                    color: '#cdd6f4' 
                                }
                            }}
                        >
                            {copied ? 
                                <CheckIcon fontSize="small" /> : 
                                <ContentCopyIcon fontSize="small" />
                            }
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ padding: 2 }}>
                    <Typography 
                        variant="body2" 
                        fontSize={14}
                        sx={{ 
                            color: '#fab387',
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            backgroundColor: '#313244',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            display: 'inline-block'
                        }}
                    >
                        {exampleFormat}
                    </Typography>
                </Box>
            </Box>

            <Typography variant='body2' marginBottom={1} fontSize={14} fontWeight={600}>Example</Typography>
            <Box sx={{ 
                backgroundColor: '#383843ff',
                border: '1px solid #45475a',
                borderRadius: 2,
                overflow: 'hidden',
                marginBottom: 2
            }}>
                <Box sx={{ 
                    backgroundColor: '#35353fff',
                    borderBottom: '1px solid #45475a',
                    px: 2,
                    py: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography 
                        variant="body2" 
                        fontSize={12}
                        sx={{ 
                            color: '#a6adc8',
                            fontWeight: 500
                        }}
                    >
                        Example Timestamp
                    </Typography>
                    <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
                        <IconButton 
                            size="small" 
                            onClick={() => handleCopy(exampleTimestamp)}
                            sx={{ 
                                color: '#a6adc8',
                                '&:hover': { 
                                    backgroundColor: '#363742ff',
                                    color: '#cdd6f4' 
                                }
                            }}
                        >
                            {copied ? 
                                <CheckIcon fontSize="small" /> : 
                                <ContentCopyIcon fontSize="small" />
                            }
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ padding: 2 }}>
                    <Typography variant="body2" fontSize={12} sx={{ color: '#a6adc8', mb: 1 }}>
                        If an alert occurred on June 5, 2025, at 1:04:48 PM, the timestamp should be sent as:
                    </Typography>
                    <Typography 
                        variant="body2" 
                        fontSize={14}
                        sx={{ 
                            color: '#bbe0a2ff',
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            backgroundColor: '#40414bff',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            display: 'inline-block'
                        }}
                    >
                        {exampleTimestamp}
                    </Typography>
                </Box>
            </Box>

            <Typography variant='body2' marginBottom={1} fontSize={14} fontWeight={600}>Format Breakdown</Typography>
            <Box sx={{ 
                backgroundColor: '#33333cff',
                border: '1px solid #45475a',
                borderRadius: 2,
                overflow: 'hidden',
                marginBottom: 2
            }}>
                <Box sx={{ 
                    backgroundColor: '#292930ff',
                    borderBottom: '1px solid #45475a',
                    px: 2,
                    py: 1
                }}>
                    <Typography 
                        variant="body2" 
                        fontSize={12}
                        sx={{ 
                            color: '#a6adc8',
                            fontWeight: 500
                        }}
                    >
                        Components
                    </Typography>
                </Box>
                <Box sx={{ padding: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ color: '#92c5f8', fontFamily: 'monospace', fontSize: 12, minWidth: '60px' }}>
                                YYYY
                            </Typography>
                            <Typography sx={{ color: '#cdd6f4', fontSize: 12 }}>
                                4-digit year
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ color: '#92c5f8', fontFamily: 'monospace', fontSize: 12, minWidth: '60px' }}>
                                MM
                            </Typography>
                            <Typography sx={{ color: '#cdd6f4', fontSize: 12 }}>
                                2-digit month (01-12)
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ color: '#92c5f8', fontFamily: 'monospace', fontSize: 12, minWidth: '60px' }}>
                                DD
                            </Typography>
                            <Typography sx={{ color: '#cdd6f4', fontSize: 12 }}>
                                2-digit day (01-31)
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ color: '#92c5f8', fontFamily: 'monospace', fontSize: 12, minWidth: '60px' }}>
                                hh
                            </Typography>
                            <Typography sx={{ color: '#cdd6f4', fontSize: 12 }}>
                                2-digit hour (00-23, 24-hour format)
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ color: '#92c5f8', fontFamily: 'monospace', fontSize: 12, minWidth: '60px' }}>
                                mm
                            </Typography>
                            <Typography sx={{ color: '#cdd6f4', fontSize: 12 }}>
                                2-digit minute (00-59)
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ color: '#92c5f8', fontFamily: 'monospace', fontSize: 12, minWidth: '60px' }}>
                                ss
                            </Typography>
                            <Typography sx={{ color: '#cdd6f4', fontSize: 12 }}>
                                2-digit second (00-59)
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default TimestampFormat;
